import React from "react";
import UlLi, { scrollTo, mountCode, initDeepLinkData, urlChangeHandler } from "js/utilities/sdk-doc-utils";
import { Grid, Row, Col, Table, Label, Glyphicon } from "react-bootstrap";
import { Navigation, Link } from "react-router";
import SDKSidebar from "js/utilities/sdk-sidebar";
import UserHandler from "js/utilities/user-handler";
import CodeBlocks from "js/utilities/sdk-doc-code-blocks";
import { ZOOM_ICON } from "js/config.jsx";

export default React.createClass({
  displayName: "zoom-client-side-security-methodologies",
  scrollToGuide: null,
  scrollingEl: null,
  getInitialState: function () {
    var scrollToElement = initDeepLinkData(this.props);
    return {
      scrollToElement: scrollToElement
    };
  },

  componentDidMount: function () {
    mountCode(this);
  },

  componentWillMount() {
    window.addEventListener('popstate', this.urlChanged, false)
  },

  urlChanged() {
    urlChangeHandler();
  },

  render: function () {

    if (this.scrollToGuide && this.scrollingEl)
      scrollTo(this.scrollingEl, this.scrollToGuide);
    this.scrollToGuide = null;

    return (
      <div>
        <div className="dash">
          <div className="workspace">
            <div className="workspace__container webservice-documentation">

              <section>
                <div className="title">
                  <Glyphicon glyph="list" />
                  <h2 id="client-side-security-myths-and-discussion">Client-Side Security Myths and Discussion</h2>
                </div>
                <p>Learn about client-side security and how FaceTec provides superior security to device-centric authentication methods.</p>
                <hr/>
              </section>

              <section>
                <h3 id="introduction">Introduction</h3>
                <p><strong>The Fundamental Question:</strong>"Can you ever trust a Client-Side Application 100%?"</p>
                <p><strong>The Answer:</strong>  No, and no matter what anyone tells you, when an attacker has control of a device there is no way to 100% guarantee it has not been compromised.  Because of this, FaceTec must assume that every client-side iOS/Android/PC is compromised, rooted, has malware, or is under complete control of a Bad Actor.  See the <Link to="security-best-practices">Security Best Practices</Link> page to learn how FaceTec protects against Client-Side vulnerabilities.</p>
                <hr/>
              </section>


              <section>
                <h3 id="security-myths">Security Myths</h3>

                <h4 id="on-device-biometric-sensors-are-the-only-way-to-ensure-biometric-data-is-not-stolen-and-leveraged-against-the-user">"On-device biometric sensors are the only way to ensure biometric data isn’t stolen and leveraged against the user."</h4>
                <p><strong>Reality:</strong> On-Device biometric storage isn’t more secure, it’s flying blind.</p>
                <p>On-Device biometrics temporarily link a User to a device, Server-Side biometrics permanently bind the User to their account.  If a phone’s on device biometric lock can be overridden by a Pin# then you know if it’s the actual User, someone they allowed to enroll or someone that had their pin or a bad actor that has taken control of their device.  On-Device Biometric data storage has been promoted for many years by FIDO and other on-device proponents who claim that the User’s device is the only place that biometric data can be safely stored. </p>

                <p>FaceTec contends that since everything on the client can be rooted/bypassed/hacked, there is nothing preventing an attacker with enough knowledge/time from finding where the Liveness result is returned from a biometrics provider and flipping the register from false to true and passing Liveness. No one at FIDO talks about this.</p>

                <hr/>

                <h4 id="myth2-trusted-execution-environments-or-secure-exclaves-solve-on-device-problems">Myth 2: "Trusted Execution Environments (TEEs) or Secure Exclaves Solve On-Device Problems."</h4>
                <p><strong>Reality:</strong>  All storage methods must be assumed to be able to be breached, so the real problem isn’t where the data is stored, it’s if that data could be used for anything if it is stolen.</p>
                <p>Some think that simply because you can use the TEE that "TEEs fix the Liveness result flip problem".  This is not true:</p>
                <ul>
                  <li><p>TEEs don't actually run biometrics algorithms, they just encrypt/decrypt data.</p></li>
                  <li><p>TEEs are device/platform specific.  Even if there was a solution, special code needs to be written for every combination.</p></li>
                  <li><p>Not So "Unhackable hardware" (part 1) - <a href="https://www.macrumors.com/2017/08/18/hacker-releases-decryption-key-secure-enclave/" target="_blank">Secure Enclave Hack</a></p></li>
                  <li><p>Not So "Unhackable hardware" (part 2) - <a href="https://blog.malwarebytes.com/mac/2019/09/new-ios-exploit-checkm8-allows-permanent-compromise-of-iphones/" target="_blank">Checkm8</a></p></li>
                </ul>
                <hr/>

                <h4 id="fido-certified-means-something-important-about-the-security-of-an-authenticator">"FIDO Certified" Means Something Important about the security of an authenticator.</h4>
                <p><strong>Reality:</strong> FIDO UAF/U2F protocols do NOT specify anything about biometric security.</p>
                <p>In fact, a valid FIDO-certified product can literally be a button that you press. Repeat, FIDO mostly relates to the communication protocols that should be used to create one means of a secure communication from an app to a relying party.</p>
                <p>Warning: FIDO now also has a biometric "certification" for Liveness.  This is NOT the Same Certification as FaceTec has achieved in the iBeta/NIST 30107-3 PAD Certification tests.  This FIDO certification for presentation attack detection is considerably lower than the iBeta tests -- 1 in 5 spoofs are allowed to bypass the system and FIDO would still certify the security of the Liveness of the product. Samsung Galaxy S10 became the first FIDO PAD certified fingerprint reader to be certified by FIDO and also became first FIDO certified authenticator to be spoofed publically just days after its release.</p>
                <hr/>

                <h4 id="myth4-browser-apps-are-inherently-less-secure-than-native-ios-android-apps">Myth 4: "Browser Apps are inherently less secure than Native iOS/Android Apps"</h4>
                <p><strong>Reality:</strong> This is not the case, both Browser-based and Native Apps have the same genres of attacks.</p>
                <p>In particular, developers/customers should realize that native iOS/Android apps are just as easily decompiled, breakpoints set, values modified, values from variables read, as in the Browser.</p>
                <ul>
                  <li>In the Browser, you have Dev Tools built into the browsers themselves -- this is a nice convenience.</li>
                  <li>iOS and Android is only a layer of obfuscation + "the attacker needs to learn the tools", while this means iOS and Android are "harder to hack" by some non-zero amount, it does not mean the platforms are more secure in a real way.</li>
                </ul>

                <hr/>

                <h4 id="myth5-call-and-response-liveness-detection-provides-tangible-real-security-benefits">Myth 5: "Call and response" Liveness Detection provides tangible/real security benefits."</h4>
                <p><strong>Reality:</strong>  False.  User "responses" are always, in some way, able to be manipulated on the client, are usually not captured concurrently with the data needed for Matching.  Call and response mechanisms add extra friction for real users with no real security benefits.</p>
                <p><strong>Examples:</strong></p>
                <ul>
                  <li>Users eyes must follow a bouncing ball as it takes a random path around the screen.</li>
                  <li>Users must perform an action like blink, smile, look over their right shoulder or over their left shoulder.</li>
                  <li>Client-side code reads reflections from a flashing light.</li>
                </ul>

                <p><strong>Workarounds:</strong></p>
                <p>These only add a layer of obfuscation.  An elite hacker who has fully reverse engineered the App and has root access to the device can manipulate code and bypass these types of checks.</p>
              </section>

              <SDKSidebar menuType="main" title="Client-Side Security Methodologies" />

            </div>
          </div>
        </div>
      </div>
    );
  }
});



// WEBPACK FOOTER //
// ./src/js/components/screens/zoom-client-side-security-methodologies.jsx